import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Alert, Typography, Tooltip, Chip,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventIcon from '@mui/icons-material/Event';
import { finalSignOff } from '../../services/api';

function parseDMY(str) {
  if (!str) return null;
  try {
    const [d, m, y] = str.split('-');
    const dt = new Date(`${m} ${d} ${y}`);
    return isNaN(dt.getTime()) ? null : dt;
  } catch { return null; }
}

export default function FinalSignOffBar({ appId, appName, isSignedOff, signoffBy, signoffAt, pendingCount = 0, dueDate, onSignOff }) {
  const hasPending = pendingCount > 0;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await finalSignOff(appId);
      onSignOff();
      setDialogOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (isSignedOff) {
    return (
      <Alert
        severity="info"
        icon={<LockOutlinedIcon sx={{ fontSize: 20 }} />}
        sx={{
          mb: 3,
          bgcolor: '#EBF5FF',
          color: '#0D47A1',
          border: '1px solid #90CAF9',
          '& .MuiAlert-icon': { color: '#0D47A1' },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Final Sign-Off completed for <strong>{appName}</strong> by {signoffBy} on {new Date(signoffAt).toLocaleDateString()}. This review is now locked and no further changes can be made.
        </Typography>
      </Alert>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: 2,
          bgcolor: hasPending ? '#FFF8E1' : '#F1F8E9',
          border: `1px solid ${hasPending ? '#FFE082' : '#C5E1A5'}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <WarningAmberIcon sx={{ color: hasPending ? '#F57F17' : '#558B2F', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
          <Box>
            {hasPending ? (
              <Typography variant="body2" sx={{ color: '#5A6A7A' }}>
                <strong style={{ color: '#E65100' }}>{pendingCount} review{pendingCount > 1 ? 's' : ''} still pending.</strong>
                {' '}Please retain or revoke all users before submitting the Final Sign-Off.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#5A6A7A' }}>
                <strong style={{ color: '#2E7D32' }}>All reviews are complete.</strong>
                {' '}You can now submit the <strong>Final Sign-Off</strong> to lock this review.
              </Typography>
            )}
            {dueDate && (() => {
              const dt = parseDMY(dueDate);
              const diff = dt ? Math.ceil((dt - new Date()) / (1000 * 60 * 60 * 24)) : null;
              const isOverdue = diff !== null && diff < 0;
              const isClose   = diff !== null && !isOverdue && diff <= 7;
              const color = isOverdue ? '#C62828' : isClose ? '#E65100' : '#2E7D32';
              const chip  = isOverdue
                ? { label: 'Overdue',       bg: '#FFEBEE', fg: '#C62828' }
                : isClose
                ? { label: `${diff}d left`, bg: '#FFF3E0', fg: '#E65100' }
                : null;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                  <EventIcon sx={{ fontSize: 13, color }} />
                  <Typography variant="caption" sx={{ color: '#5A6A7A', fontSize: '0.72rem' }}>
                    Due by: <strong style={{ color }}>{dueDate}</strong>
                  </Typography>
                  {chip && (
                    <Chip label={chip.label} size="small"
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, borderRadius: 1, bgcolor: chip.bg, color: chip.fg }} />
                  )}
                </Box>
              );
            })()}
          </Box>
        </Box>
        <Tooltip
          title={hasPending ? `${pendingCount} pending review(s) must be resolved before sign-off` : ''}
          arrow
        >
          <span>
            <Button
              variant="contained"
              startIcon={<GavelIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={hasPending}
              sx={{
                bgcolor: '#0A1929',
                '&:hover': { bgcolor: '#1A3A5C' },
                '&.Mui-disabled': { bgcolor: '#B0BEC5', color: '#fff', cursor: 'not-allowed' },
                px: 3,
                whiteSpace: 'nowrap',
              }}
            >
              Final Sign-Off
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0A1929', pb: 1 }}>
          Confirm Final Sign-Off
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#5A6A7A' }}>
            You are about to finalize the access review for <strong>{appName}</strong>.
            Once submitted, <strong>no further changes</strong> can be made to user retain or revoke decisions for this review cycle.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2, fontSize: '0.8rem' }}>
            This action is irreversible. Please ensure all user access decisions are correct before proceeding.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            sx={{ color: '#5A6A7A' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#0A1929', '&:hover': { bgcolor: '#1A3A5C' }, px: 3 }}
          >
            {loading ? 'Processing...' : 'Confirm Sign-Off'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
